import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../config/api';
import {
  BookOpenIcon,
  Square3Stack3DIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  BuildingOffice2Icon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface CourseItem {
  id: string;
  item_type: 'reading' | 'assignment';
  position: number;
  book_id?: string | null;
  page_start?: number | null;
  page_end?: number | null;
  assignment_id?: string | null;
  book_title?: string | null;
  book_cover_url?: string | null;
  assignment_title?: string | null;
  completed?: boolean | null;
}
interface Course { id: string; title: string; description?: string | null; status: string; item_count?: number | null; completed_count?: number | null; }
interface CourseDetail extends Course { items: CourseItem[]; }

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
const Spinner: React.FC = () => <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-500" />;
const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600 dark:text-cyan-400">{children}</div>
);
const BookCover: React.FC<{ url?: string | null; title?: string | null; className?: string }> = ({ url, title, className }) => {
  const [err, setErr] = useState(false);
  if (url && !err) return <img src={url} alt={title || ''} onError={() => setErr(true)} className={cx('object-cover', className)} />;
  return (
    <div className={cx('flex items-center justify-center bg-gradient-to-br from-cyan-700 via-cyan-500 to-sky-400 text-white/90', className)}>
      <BookOpenIcon className="h-1/3 w-1/3" />
    </div>
  );
};

// ── My Courses list ───────────────────────────────────────────────────────
const MyCoursesList: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/courses/enrolled').then((r) => setCourses(r.data)).catch(() => setCourses([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-8 py-10">
      <Eyebrow>My Courses</Eyebrow>
      <h1 className={cx('mt-2 text-3xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>Your courses</h1>
      <div className="mt-8 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : courses.length === 0 ? (
          <div className={cx('rounded-2xl border border-dashed p-12 text-center', isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-gray-200 bg-white')}>
            <Square3Stack3DIcon className="mx-auto h-10 w-10 text-cyan-500" />
            <h3 className={cx('mt-4 text-lg font-bold', isDarkMode ? 'text-white' : 'text-gray-900')}>No courses yet</h3>
            <p className={cx('mx-auto mt-1.5 max-w-sm text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Courses your teachers assign will appear here, ready to work through in order.</p>
          </div>
        ) : (
          courses.map((c) => {
            const tot = c.item_count ?? 0;
            const dn = c.completed_count ?? 0;
            const p = tot ? Math.round((dn / tot) * 100) : 0;
            const allDone = tot > 0 && dn === tot;
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/my-courses/${c.id}`)}
                className={cx('group flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition hover:-translate-y-0.5', isDarkMode ? 'border-slate-700 bg-slate-900/50 hover:border-cyan-500/40' : 'border-gray-200 bg-white hover:border-cyan-500/40 hover:shadow-md')}
              >
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><Square3Stack3DIcon className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className={cx('truncate font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{c.title}</div>
                  <div className={cx('text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>
                    {allDone ? 'Complete 🎉' : `${dn} of ${tot} ${tot === 1 ? 'item' : 'items'}`}
                  </div>
                  {tot > 0 && (
                    <div className={cx('mt-2 h-1.5 w-full overflow-hidden rounded-full', isDarkMode ? 'bg-slate-800' : 'bg-gray-100')}>
                      <div className="h-full rounded-full bg-cyan-500 transition-all duration-500" style={{ width: `${p}%` }} />
                    </div>
                  )}
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-cyan-500" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// ── Student course view (the guided sequence) ─────────────────────────────
const StudentCourseView: React.FC<{ courseId: string }> = ({ courseId }) => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/api/courses/enrolled/${courseId}`).then((r) => setCourse(r.data)).catch(() => setCourse(null)).finally(() => setLoading(false));
  }, [courseId]);

  if (loading) return <div className="flex justify-center py-24"><Spinner /></div>;
  if (!course) return <div className={cx('mx-auto max-w-md px-8 py-24 text-center', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Course not found, or it isn't assigned to you.</div>;

  const openReading = (item: CourseItem) => {
    if (item.book_id) navigate(`/dhyana/${item.book_id}?page=${item.page_start ?? 1}&end=${item.page_end ?? ''}&course=${courseId}&item=${item.id}`);
  };

  const total = course.items.length;
  const completedCount = course.items.filter((i) => i.completed).length;
  const currentIndex = course.items.findIndex((i) => !i.completed); // first unfinished; -1 when all done
  const pct = total ? Math.round((completedCount / total) * 100) : 0;

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <button onClick={() => navigate('/my-courses')} className={cx('mb-4 inline-flex items-center gap-1.5 text-sm font-medium', isDarkMode ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900')}>
        <ArrowLeftIcon className="h-4 w-4" /> My Courses
      </button>
      <Eyebrow>Course</Eyebrow>
      <h1 className={cx('mt-2 text-3xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>{course.title}</h1>
      {course.description && <p className={cx('mt-2 max-w-xl text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>{course.description}</p>}
      <p className={cx('mt-1 text-sm', isDarkMode ? 'text-slate-500' : 'text-gray-400')}>{course.items.length} item{course.items.length === 1 ? '' : 's'} · work through them in order</p>

      {total > 0 && (
        <div className="mt-5">
          <div className={cx('flex items-center justify-between text-xs font-medium', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>
            <span>{completedCount === total ? 'Course complete 🎉' : `${completedCount} of ${total} complete`}</span>
            <span>{pct}%</span>
          </div>
          <div className={cx('mt-1.5 h-2 w-full overflow-hidden rounded-full', isDarkMode ? 'bg-slate-800' : 'bg-gray-100')}>
            <div className="h-full rounded-full bg-cyan-500 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <div className="mt-8 space-y-3 pb-8">
        {course.items.map((item, idx) => {
          const reading = item.item_type === 'reading';
          const done = !!item.completed;
          const isCurrent = idx === currentIndex;
          return (
            <div key={item.id} className="flex items-stretch gap-4">
              <div className="flex flex-none flex-col items-center pt-4">
                <span className={cx(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
                  done
                    ? 'bg-cyan-500 text-white'
                    : isCurrent
                      ? 'border-2 border-cyan-500 bg-white text-cyan-700 ring-4 ring-cyan-500/20 dark:bg-slate-900 dark:text-cyan-300'
                      : cx('border-2 bg-white dark:bg-slate-900', isDarkMode ? 'border-slate-600 text-slate-500' : 'border-gray-300 text-gray-400'),
                )}>
                  {done ? <CheckIcon className="h-4 w-4" /> : idx + 1}
                </span>
                {idx < total - 1 && <span className={cx('mt-1 w-0.5 flex-1', done ? 'bg-cyan-500' : 'bg-cyan-500/20')} />}
              </div>
              <div className={cx(
                'flex flex-1 items-center gap-3 rounded-2xl border p-3.5',
                isCurrent ? 'border-cyan-500/50 ring-1 ring-cyan-500/30' : (isDarkMode ? 'border-slate-700' : 'border-gray-200 shadow-sm'),
                isDarkMode ? 'bg-slate-900/50' : 'bg-white',
              )}>
                {reading ? (
                  <BookCover url={item.book_cover_url} title={item.book_title} className="h-14 w-10 flex-none rounded-md shadow" />
                ) : (
                  <span className="flex h-14 w-10 flex-none items-center justify-center rounded-md bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><PencilSquareIcon className="h-5 w-5" /></span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">{reading ? 'Reading' : 'Assignment'}{done ? ' · Done' : ''}</div>
                  <p className={cx('truncate text-sm font-semibold', isDarkMode ? 'text-white' : 'text-gray-900')}>{reading ? item.book_title || 'Reading' : item.assignment_title || 'Assignment'}</p>
                  {reading && (item.page_start || item.page_end) && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400"><DocumentTextIcon className="h-3 w-3" /> Pages {item.page_start ?? '?'}–{item.page_end ?? '?'}</div>
                  )}
                </div>
                {reading ? (
                  <button onClick={() => openReading(item)} className={cx(
                    'inline-flex flex-none items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition',
                    done
                      ? (isDarkMode ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                      : 'bg-cyan-500 text-white hover:bg-cyan-600',
                  )}>
                    <BookOpenIcon className="h-4 w-4" /> {done ? 'Review' : 'Read'}
                  </button>
                ) : (
                  <span className={cx('flex-none rounded-lg px-3 py-1.5 text-xs font-medium', isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500')}>Coming soon</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NoTenant: React.FC = () => {
  const { isDarkMode } = useTheme();
  return (
    <div className="mx-auto max-w-xl px-8 py-20 text-center">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-300"><BuildingOffice2Icon className="h-7 w-7" /></span>
      <h2 className={cx('mt-5 text-2xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-gray-900')}>Open this on your institution</h2>
      <p className={cx('mx-auto mt-2 max-w-md text-sm', isDarkMode ? 'text-slate-400' : 'text-gray-500')}>Courses live on your institution's space. Open this page on your institution subdomain.</p>
    </div>
  );
};

const MyCourses: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { tenant } = useTenant();
  return <Layout>{!tenant?.institution_id ? <NoTenant /> : courseId ? <StudentCourseView courseId={courseId} /> : <MyCoursesList />}</Layout>;
};

export default MyCourses;
